@file:OptIn(ExperimentalUnsignedTypes::class, ExperimentalStdlibApi::class)

package ai.hypergraph.tidyparse.wgpu

import ai.hypergraph.tidyparse.wgpu.GPUBufferUsage.STCPSD
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.GPUBuffer
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.buildLanguageSizeCDF
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.packMetadata
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.readIndices
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.toGPUBuffer
import ai.hypergraph.tidyparse.wgpu.Shader.Companion.writeU32
import ai.hypergraph.kaliningraph.automata.*
import ai.hypergraph.kaliningraph.parsing.*
import ai.hypergraph.kaliningraph.types.cache
import js.buffer.*
import js.typedarrays.Int32Array
import kotlinx.coroutines.await
import web.events.*
import web.gpu.*
import kotlin.js.Promise
import kotlin.math.*
import kotlin.reflect.KProperty
import kotlin.time.*
import kotlin.js.Promise as KPromise


lateinit var gpu: GPUDevice
var gpuAvailable = false
external val navigator: dynamic
typealias JSIntArray = Int32Array<ArrayBuffer>

/*
  TODO:
    (1) Minimize repairs on GPU
    (2) Use more sensible midpoint filter based on dense adjacency / reachability matrix
    (3) Implement test harness to avoid regressions. Need to measure: (1) perf (2) prec@k
*/

const val largeMem = "{ requiredLimits: { maxBufferSize: 2000000000, maxStorageBufferBindingSize: 2000000000, maxStorageBuffersPerShaderStage: 10 } }"
const val smallMem = "{ requiredLimits: { maxBufferSize: 1073741824, maxStorageBufferBindingSize: 1073741824, maxStorageBuffersPerShaderStage: 10 } }"
const val MAX_LEV_RAD = 5 // Edit metadata has six deletion codes; the seventh code is insertion.

suspend fun tryBootstrappingGPU(needsExtraMemory: Boolean = false) {
  // Shader pipelines, grammar encodings, and model buffers are device-owned and cached.
  // Replacing a live device would leave those caches pointing at invalid foreign buffers.
  if (gpuAvailable) {
    reportRuntimeStatus("ready", "WebGPU ready")
    return
  }

  val tmpDev = try {
    (navigator.gpu as? GPU)?.requestAdapter()?.also {
      gpu = if (needsExtraMemory) it.requestDevice(js(largeMem))
      else it.requestDevice(js(smallMem))
    }
  } catch (t: Throwable) {
    gpuAvailable = false
    markWebGPUStatus("error", "WebGPU unavailable: ${t.message ?: t.toString()}")
    return
  }

  if (tmpDev != null) {
    gpu.addEventListener(EventType("uncapturederror"), { e: dynamic -> log("Uncaptured: ${e.error.message}") })
    try {
      listOf(
        // Utils
        prefix_sum_p1, prefix_sum_p2,
        // Loaders
        sparse_mat_load,
        // Automata construction
        init_lev_chart, init_line_chart,
        // Matrix closure/CFL reachability
        dag_reach, mdpt_count, mdpt_write, line_mdpt_write,
        cfl_mul_upper,
        // Counting/Enumeration
        bp_count, bp_write,
        ls_cdf, suffix_ls_dense,
        uniform_choice_counts, uniform_index,
        greedy_active_rows, greedy_choice_counts, greedy_pcfg_order,
        histogram_choice_counts, pcfg_min_cost, pcfg_scale, pcfg_choice_labels, pcfg_dense_convolve, pcfg_build_root_cdf,
        histogram_compile_row_counts, histogram_compile_row_map, histogram_compile_nodes, histogram_compile_roots,
        build_root_sizes, enum_words_wor, suffix_enum_words_wor,
        // Sampling
        markov_score, wdfa_score, select_top_k, merge_top_k, gather_top_k, suffix_group_select, // rerank_top_k,
        // Debugging
        wdfa_score_raw, active_nt_count,
      ).forEach { it.bind() }
//      benchmarkWGPU() // TODO: remove for deployment
//      benchmarkWGPURepair()
//      benchmarkReach()
    } catch (e: Exception) {
      e.printStackTrace()
      markWebGPUStatus("error", "WebGPU unavailable: ${e.message ?: e.toString()}")
      return
    }

    log("Bootstrapping GPU successful!")
    gpuAvailable = true

    markWebGPUStatus("ready", "WebGPU ready")
  } else {
    markWebGPUStatus("error", "WebGPU unavailable")
    print("GPU not detected.")
  }
}

private fun markWebGPUStatus(state: String, label: String) {
  reportRuntimeStatus(state, label)
}

suspend fun repairCode(
  cfg: CFG,
  code: List<String>,
  ledBuffer: Int = Int.MAX_VALUE,
  rerankerQuery: List<String>? = null,
  reranker: RepairRerankerCallback? = null,
  requestStarted: TimeMark = TimeSource.Monotonic.markNow()
): IntersectionResults {
  timings = linkedMapOf()
  mark("preprocessing", requestStarted)
  val preprocT = TimeSource.Monotonic.markNow()
  val fsa: FSA = makeLevFSA(code, MAX_LEV_RAD)
  log("Made levFSA in ${preprocT.elapsedNow()}")

  // TODO: maybe modify the LevFSA to accommodate unknown tokens?
  val codePoints = IntArray(code.size) { cfg.tmMap[code[it]] ?: 0 }

// This is interchangeable with init_chart for Lev automata
//  val dpInSparse = fsa.byteFormat(cfg).toGPUBuffer()
//  log("Initial nonzeros: ${dpIn.count { it != 0 }}")

  mark("build input FSA", preprocT)
//  val words = intersectionPipelineV2(cfg, fsa, ledBuffer, codePoints, rerankerQuery)
  val words = intersectionPipeline(cfg, fsa, ledBuffer, codePoints, rerankerQuery, reranker)
//  val distinctWords = words.distinct()
//  log("Distinct: ${distinctWords.size} words")

  return words.also { log("Received: ${words.size} words in ${requestStarted.elapsedNow()} (round trip)") }
}

suspend fun intersectionPipeline(
  cfg: CFG,
  fsa: FSA,
  ledBuffer: Int,
  codePoints: IntArray,
  rerankerQuery: List<String>? = null,
  reranker: RepairRerankerCallback? = null,
  chartInitializer: Shader = init_lev_chart
): IntersectionResults = GPUBufferScope().use { buffers ->
  require(cfg.tmLst.size < PACKED_TOKEN_LIMIT) {
    "Packed repair packets support fewer than $PACKED_TOKEN_LIMIT terminals"
  }
  val (numStates, numNTs) = fsa.numStates to cfg.nonterminals.size
  log("FSA(|Q|=$numStates, |δ|=${fsa.transit.size}), ${cfg.calcStats()}")

  val metaT = TimeSource.Monotonic.markNow()
  val metaBuf = buffers.own(packMetadata(cfg, fsa))
  mark("pack metadata", metaT)
  log("Packed metadata in ${timings["pack metadata"]}ms")

  val tmBuf       = cfg.termBuf // Borrowed from the CFG cache; never destroy per invocation.
  val wordBuf     = buffers.own(codePoints.toGPUBuffer())
  val totalSize   = numStates * numStates * numNTs
  val activeWords = (numNTs + 31) ushr 5

  val dpBuf     = buffers.own(Shader.createParseChart(STCPSD, totalSize))
  val activeBuf = buffers.newBuffer((numStates * numStates * activeWords * 4).toLong())

  log(
    "Buffers: dp=${dpBuf.size}B (${totalSize} cells), " +
    "active=${activeBuf.size}B (${activeWords} words/cell), " +
    "word=${wordBuf.size}B, tm=${tmBuf.size}B"
  )

  timings["init chart"] = timedGPUIsolated("Init chart") {
    val ntWorkgroups = (numNTs + DENSE_NT_WORKGROUP_SIZE - 1) / DENSE_NT_WORKGROUP_SIZE
    chartInitializer(dpBuf, activeBuf, wordBuf, metaBuf, tmBuf)(numStates, numStates, ntWorkgroups)
  }

  val closureT = TimeSource.Monotonic.markNow()

  cfl_mul_upper.invokeCFLFixpoint(numStates, dpBuf, activeBuf, metaBuf)
  buffers.release(activeBuf, wordBuf)

  mark("matrix closure", closureT)
  log("Matrix closure reached in: ${timings["matrix closure"]}ms")

  val rootsT = TimeSource.Monotonic.markNow()
  val startNT     = cfg.bindex[START_SYMBOL]
  val rootQuery   = fsa.finalIdxs.map { it * numNTs + startNT }
  val rootReachability = dpBuf.readIndices(rootQuery)
  val allStartIds = rootQuery.filterIndexed { i, _ -> rootReachability[i] != 0 }
  mark("read roots", rootsT)

  if (allStartIds.isEmpty()) {
//    timings.logTimingsToJSConsole()
    return@use IntersectionResults.EMPTY.also { log("No valid parse found: dpComplete has no entries in final states!") }
  }

  log("Valid parse found: dpComplete has ${allStartIds.size} start indices")

  val bpT = TimeSource.Monotonic.markNow()
  val (bpCountBuf, bpOffsetBuf, bpStorageBuf) = Shader.buildBackpointers(numStates, numNTs, dpBuf, metaBuf)
  buffers.own(bpCountBuf, bpOffsetBuf, bpStorageBuf)
  mark("build backpointers", bpT)
  val totalExp = bpStorageBuf.size.toInt() / (2 * 4)
  log("Built backpointers in ${timings["build backpointers"]}ms | expansions=$totalExp")

  val t2 = TimeSource.Monotonic.markNow()
  val statesToDist = allStartIds.map {
    val (i, j) = fsa.idsToCoords[(it - startNT) / numNTs]!!
    it to j + codePoints.size - i
  }
  val led = statesToDist.minOf { it.second }

  val maxAcceptedDistance = (led.toLong() + ledBuffer).coerceAtMost(Int.MAX_VALUE.toLong()).toInt()
  val startIdxs = statesToDist
    .filter { it.second in led..maxAcceptedDistance }
    .map { listOf(it.first, it.second) }
    .sortedBy { it[1] }
    .also { log("Start indices: total=${it.size}, roots=${it.size}, LED=$led, window=[$led, $maxAcceptedDistance]") }
    .flatten()

  mark("filter roots", t2)

  val maxRepairLen = fsa.width + fsa.height + 10
  if (MAX_WORD_LEN < maxRepairLen) {
//    timings.logTimingsToJSConsole()
    return@use IntersectionResults.EMPTY.also { log("Max repair length exceeded $MAX_WORD_LEN ($maxRepairLen)") }
  }

  val cdfBuf = buffers.newBuffer(maxOf(4, totalExp * 4))
  if (PROFILE_WGPU_KERNELS) awaitGPUQueue()
  val cdfT = TimeSource.Monotonic.markNow()
  buildLanguageSizeCDF(
    numStates, numNTs, dpBuf, metaBuf, tmBuf,
    bpCountBuf, bpOffsetBuf, bpStorageBuf, cdfBuf
  )
  if (PROFILE_WGPU_KERNELS) awaitGPUQueue()
  mark("build language-size CDF", cdfT)
  log("Built language-size CDF in ${timings["build language-size CDF"]}ms (${cdfBuf.size}B)")
  buffers.release(metaBuf)

  val numRoots = startIdxs.size / 2
  val rootSizes = buffers.newBuffer(numRoots * 4)

  /** Memory layout: [IDX_UNIFORM_STRUCT] */
  val idxUniBuf = buffers.own(packStruct(
    listOf(0, maxRepairLen, numNTs, numStates, DISPATCH_GROUP_SIZE_X, MAX_SAMPLES),
    startIdxs.toGPUBuffer()
  ))

  timings["build root sizes"] = timedGPUIsolated("Build root sizes (roots=$numRoots)") {
    build_root_sizes(dpBuf, bpCountBuf, bpOffsetBuf, cdfBuf, tmBuf, rootSizes, idxUniBuf)((numRoots + 255) / 256)
  }

  val rootCDFTime = TimeSource.Monotonic.markNow()
  val rootCDF = buffers.own(Shader.prefixSumGPU(rootSizes, numRoots))
  mark("prefix root cdf", rootCDFTime)
  log("Built root CDF in ${timings["prefix root cdf"]}ms (${rootCDF.size}B)")

  val langSizeT = TimeSource.Monotonic.markNow()
  suspend fun langIntSize(): Long {
    fun u(x: Int) = x.toUInt().toLong()
    val last = numRoots - 1
    val lastSize = if (numRoots > 0) rootSizes.readIndices(listOf(last))[0] else 0
    val lastCDF = if (numRoots > 0) rootCDF.readIndices(listOf(last))[0] else 0
    return if (numRoots > 0) u(lastCDF) + u(lastSize) else 0L
  }

  val langIntSize = langIntSize()
  mark("read lang size", langSizeT)

  val toDecode = minOf(MAX_SAMPLES.toLong(), langIntSize).toInt()
  if (toDecode == 0) return@use IntersectionResults.EMPTY
  idxUniBuf.writeU32(wordIndex = 5, value = toDecode)
  val indexT = TimeSource.Monotonic.markNow()

  val bijection = greedyPCFGIndex(numStates, numNTs, dpBuf, bpCountBuf, bpOffsetBuf, bpStorageBuf, cdfBuf, tmBuf, idxUniBuf, cfg.pcfgBuf)
//   val bijection = uniformIndex(numStates, numNTs, dpBuf, bpCountBuf, bpOffsetBuf, bpStorageBuf, cdfBuf, tmBuf, idxUniBuf)
//   val bijection = histogramSemiringIndex(numStates, numNTs, dpBuf, bpCountBuf, bpOffsetBuf, bpStorageBuf, cdfBuf, tmBuf, idxUniBuf, cfg.pcfgBuf)

  buffers.own(bijection)
  if (PROFILE_WGPU_KERNELS) awaitGPUQueue()
  mark("build word bijection", indexT)
  log("Build word bijection: ${timings["build word bijection"]}ms")
  buffers.release(dpBuf, bpCountBuf, bpOffsetBuf, bpStorageBuf, cdfBuf, rootSizes, rootCDF)

  val pct = toDecode.toDouble() * 100.0 / langIntSize.toDouble().coerceAtLeast(1.0)
  log("Language saturation: $pct% ($toDecode/$langIntSize), maxRepairLen=$maxRepairLen")
  val outBuf = buffers.newBuffer(toDecode * maxRepairLen * 4)
  timings["enumerate"] = timedGPUIsolated("Enumerate") {
    enum_words_wor(bijection, idxUniBuf, outBuf).dispatchFlat(toDecode)
  }
  log("Enumerated $toDecode samples in ${timings["enumerate"]}ms (${outBuf.size}B)")
  buffers.release(bijection, idxUniBuf)

  val decodeT = TimeSource.Monotonic.markNow()
  val shouldRerank = rerankerQuery != null && reranker != null
  val decoderTopK = if (shouldRerank) RERANKER_TOP_K_SAMP else TOP_K_SAMP
  val result =
    if (wdfa != null) wdfaDecoder(outBuf, wdfa!!, maxRepairLen, cfg, toDecode, decoderTopK)
    else if (ngrams != null) ngramDecoder(outBuf, ngrams!!, maxRepairLen, cfg, toDecode, decoderTopK)
    else uniformDecoder(outBuf, cfg, maxRepairLen, toDecode)
  mark("decode", decodeT)

  if (shouldRerank) {
    val candidates = result.takeResults(RERANKER_TOP_K_SAMP)
    val rerankT = TimeSource.Monotonic.markNow()
    reranker(rerankerQuery, candidates)
      .let(candidates::selectResults)
      .also { mark("rerank", rerankT) }
  } else result
}

suspend fun GPUBuffer.readJSIntArray(): JSIntArray {
  val readDst = GPUBuffer(size.toInt(), GPUBufferUsage.COPY_DST or GPUBufferUsage.MAP_READ)

  val cmd = gpu.createCommandEncoder()
  cmd.copyBufferToBuffer(this, 0.0, readDst, 0.0, size)
  gpu.queue.submit(arrayOf(cmd.finish()))

  readDst.mapAsync(GPUMapMode.READ).unsafeCast<Promise<*>>().await()

  val mapped = readDst.getMappedRange()
  val copied = mapped.slice(0, mapped.byteLength)

  readDst.unmap()
  readDst.destroy()

  return Int32Array(copied)
}

suspend fun uniformDecoder(outBuf: GPUBuffer, cfg: CFG, maxRepairLen: Int, samplesToDecode: Int): IntersectionResults =
  decodePackets(outBuf.readJSIntArray(), cfg, maxRepairLen, samplesToDecode.also { log("Using uniform decoder...") })

// TODO: Switch to MRF-based sampler? https://aclanthology.org/H92-1028.pdf
suspend fun ngramDecoder(
  outBuf: GPUBuffer,
  ngrams: GPUBuffer,
  maxRepairLen: Int,
  cfg: CFG,
  samplesToDecode: Int,
  k: Int = TOP_K_SAMP
): IntersectionResults {
  log("Using n-gram decoder...")
  val topK = scoreSelectGather(outBuf, ngrams, markov_score, samplesToDecode, maxRepairLen, k)
  return decodePackets(topK, cfg, maxRepairLen)
}

suspend fun wdfaDecoder(
  outBuf: GPUBuffer,
  wdfa: GPUBuffer,
  maxRepairLen: Int,
  cfg: CFG,
  samplesToDecode: Int,
  k: Int = TOP_K_SAMP
): IntersectionResults {
  log("Using WDFA decoder...")
  val topK = scoreSelectGather(
    outBuf, wdfa, wdfa_score, samplesToDecode, maxRepairLen, k,
    profileLabel = "WDFA score"
  )
  return decodePackets(topK, cfg, maxRepairLen)
}

private fun editTieBreakPriority(op: Int): Int = when (op) {
  LEV_EDIT_INSERT -> 0
  LEV_EDIT_DELETE -> 1
  else -> 2 // Match/substitution share the diagonal transition.
}

/** Uses the same backward insertion/deletion/diagonal tie-break as [levenshteinAlign]. */
private fun IntArray.isPreferredTo(other: IntArray): Boolean {
  if (this[0] != other[0]) return this[0] < other[0]

  val edits = unpackEdits()
  val otherEdits = other.unpackEdits()

  for (offset in 1..minOf(edits.size, otherEdits.size)) {
    val priority = editTieBreakPriority(edits[edits.size - offset])
    val otherPriority = editTieBreakPriority(otherEdits[otherEdits.size - offset])
    if (priority != otherPriority) return priority < otherPriority
  }

  return if (edits.size != otherEdits.size) edits.size < otherEdits.size
  else this[1].toUInt() < other[1].toUInt()
}

// Copies one valid packet into a compact integer row, omitting the zero terminator and unused capacity.
internal fun JSIntArray.decodePacket(idx: Int, terminalCount: Int, pktLen: Int): IntArray? {
  val base = idx * pktLen
  val distance = this[base]
  var rowSize = PKT_HDR_LEN
  var encodedEdits = 0

  for (j in 0 until (pktLen - PKT_HDR_LEN)) {
    val packed = this[base + PKT_HDR_LEN + j].toUInt()
    if (packed == 0u) break

    val tok = (packed and PACKED_TOKEN_MASK.toUInt()).toInt()
    if (tok !in 1..terminalCount) return null
    rowSize++

    when (val editTag = (packed shr PACKED_EDIT_SHIFT).toInt()) {
      PACKED_INSERTION_TAG, PACKED_SUBSTITUTION_TAG -> encodedEdits++
      in PACKED_FIRST_DELETION_TAG..PACKED_LAST_DELETION_TAG -> {
        encodedEdits += editTag - PACKED_SUBSTITUTION_TAG
      }
    }
  }

  return if (rowSize == PKT_HDR_LEN || encodedEdits > distance) null
  else IntArray(rowSize) { this[base + it] }
}

fun decodePackets(
  packets: JSIntArray,
  cfg: CFG,
  maxRepairLen: Int,
  packetCount: Int = packets.length / maxRepairLen
): IntersectionResults {
  val t0 = TimeSource.Monotonic.markNow()
  val rows = mutableListOf<IntArray>()
  val hashBuckets = mutableMapOf<Int, MutableList<Int>>()

  for (i in 0 until packetCount) {
    val row = packets.decodePacket(i, cfg.tmLst.size, maxRepairLen) ?: continue
    val bucket = hashBuckets.getOrPut(row.tokenHash()) { mutableListOf() }
    val previous = bucket.firstOrNull { rows[it].sameTokens(row) }
    if (previous == null) {
      bucket += rows.size
      rows += row
    } else if (row.isPreferredTo(rows[previous])) rows[previous] = row
  }

  rows.map { it[0] }.distinct().sorted().forEach { distance ->
    log("Δ=$distance -> |L|=${rows.count { it[0] == distance }}")
  }

  log("Decoded ${rows.size} unique words from $packetCount packets in ${t0.elapsedNow()}")
  return IntersectionResults(rows, cfg.tmLst)
}

suspend fun scoreSelectGather(
  packets      : GPUBuffer,
  model        : GPUBuffer,
  scoreShader  : Shader,
  maxSamples   : Int,
  stride       : Int,
  k            : Int,
  profileLabel : String? = null
): JSIntArray = GPUBufferScope().use { buffers ->
  val resultCount = minOf(k, maxSamples)
  val t0 = TimeSource.Monotonic.markNow()
  val threads = DISPATCH_GROUP_SIZE_X
  /** Memory layout: [SAMPLER_PARAMS] */
  val prmBuf = buffers.own(intArrayOf(maxSamples, resultCount, stride, threads)
    .toGPUBuffer(GPUBufferUsage.UNIFORM or GPUBufferUsage.COPY_DST))

  suspend fun dispatch(label: String, block: () -> Unit) = if (profileLabel == null) block() else timedGPUIsolated(label, block)

  dispatch(profileLabel ?: "Score") { scoreShader(packets, model, prmBuf).dispatchFlat(maxSamples) }

  lateinit var idxBuf: GPUBuffer
  dispatch("Select top-k") { idxBuf = buffers.own(selectTopKIndices(packets, maxSamples, stride, resultCount)) }

  val bestBuf = buffers.newBuffer(resultCount.toLong() * stride * 4)

  dispatch("Gather top-k") { gather_top_k(prmBuf, packets, idxBuf, bestBuf).dispatchFlat(resultCount) }

  val topK = bestBuf.readJSIntArray()
  log("${profileLabel ?: "Score"}/select/gather read ${topK.length} = ${resultCount}x${stride}x4 bytes in ${t0.elapsedNow()}")

  topK
}

// Maps NTs to terminals for sampling. This buffer is cached and borrowed by each pipeline invocation.
val CFG.termBuf: GPUBuffer by cache {
//  val packTime = TimeSource.Monotonic.markNow()
  val terminalLists = nonterminals.map { bimap.UNITS[it]?.map { tmMap[it]!! } ?: emptyList() }
  val nt_tm_lens = terminalLists.map { it.size }.toGPUBuffer()
  val nt_tm_offsets = terminalLists.scan(0) { acc, list -> acc + list.size }.dropLast(1).toGPUBuffer()
  val all_tm = terminalLists.flatten().toGPUBuffer()

  /** Memory layout: [TERM_STRUCT] */ packStruct(emptyList(), nt_tm_lens, nt_tm_offsets, all_tm)
//    .also { log("Packing time: ${packTime.elapsedNow()}") }
}

private val pcfgBuffers = js("new WeakMap()") // Symbol IDs belong to this grammar instance.
internal val CFG.pcfgBuf: GPUBuffer? get() = pcfgBuffers.get(this).unsafeCast<GPUBuffer?>()

/** Load CNF productions of the form `A -> B C [numerator/denominator]` (or `A -> token [...]`). */
fun CFG.loadPCFG(text: String) {
  val nts = nonterminals.withIndex().associate { it.value to it.index }
  val rows = Array(nts.size) { mutableMapOf<Pair<Int, Int>, Pair<Int, Int>>() }
  text.lineSequence().filter { it.isNotBlank() }.forEach { line ->
    val rule = line.substringBeforeLast(" [").trim().split(Regex("\\s+"))
    val fraction = line.substringAfterLast('[').substringBefore(']').split('/')
    require(rule.size in 3..4 && rule[1] == "->" && fraction.size == 2) { "Invalid PCFG rule: $line" }
    val numerator = fraction[0].toDouble()
    val denominator = fraction[1].toDouble()
    require(numerator >= 0 && denominator > 0 && numerator <= denominator) { "Invalid PCFG probability: $line" }
    val parent = nts[rule[0]] ?: return@forEach
    val left = (if (rule.size == 3) tmMap[rule[2]] else nts[rule[2]]) ?: return@forEach
    val right = if (rule.size == 3) -1 else nts[rule[3]] ?: return@forEach
    // Keep even rare productions reachable when quantizing to thousandths.
    val weight = (1000 * numerator / denominator).roundToInt().coerceAtLeast(1)
    val cost = if (numerator == 0.0) -1 else (-ln(numerator / denominator) * SCALE).roundToInt()
    rows[parent][left to right] = weight to cost
  }
  // [row offsets (absolute, including sentinel), sorted (left, right, weight, log-cost) records]
  val data = MutableList(nts.size + 1) { 0 }
  rows.forEachIndexed { nt, row ->
    data[nt] = data.size
    row.entries.sortedWith(compareBy({ it.key.first }, { it.key.second.toUInt() }))
      .forEach { (rhs, scores) -> data.addAll(listOf(rhs.first, rhs.second, scores.first, scores.second)) }
  }
  data[nts.size] = data.size
  val previous = pcfgBuffers.get(this).unsafeCast<GPUBuffer?>()
  pcfgBuffers.set(this, data.toGPUBuffer(STCPSD))
  previous?.destroy()
}

//language=wgsl
const val CHART_DECODING_HELPERS = """
fn getStartIdx(i : u32) -> u32 { return idx_uni.startIndices[i * 2]; }
fn getEditDist(i : u32) -> u32 { return idx_uni.startIndices[i * 2 + 1]; }
"""

//language=wgsl
const val TM_DECODING_HELPERS = """
const LIT_ALL        : u32 = 0x47fffffeu;
const NEG_BIT        : u32 = $NEG_STR_LIT;
const PREDICATE_MASK : u32 = 0x47fffffeu;
const EDIT_SUB_BIT   : u32 = 0x80000000u;
const EDIT_DEL_SHIFT : u32 = 27u;
const EDIT_DEL_MASK  : u32 = 0x38000000u;
const EDIT_INSERT_CODE : u32 = 7u; // reserved maximum value of the three-bit edit field

// Let Σ_A denote the subset of Σ s.t. for all a ∈ Σ_A ⊢ (A -> a) ∈ P
fn get_nt_tm_lens(nt : u32) -> u32 { return terminals.payload[terminals.nt_tm_lens_offset + nt]; } // |Σ_A|
// Offsets of the nonterminal in the following Map<...> structure
fn get_offsets(nt : u32) -> u32    { return terminals.payload[terminals.offsets_offset + nt];    } // offset of Σ_A
// Flattened index of Map<NT, List<TM-id>> values
fn get_all_tms(i : u32) -> u32     { return terminals.payload[terminals.all_tms_offset + i];     } // σ → TM‑id
// Counts the number of terminals directly generated by a unit-nonterminal entry in the parse chart
fn count_tms(val: u32, unit_nt: u32) -> u32 {
    // Wildcard: allow any terminal in Σ_A
    let ntLen = get_nt_tm_lens(unit_nt);
    let predicate = val & PREDICATE_MASK;
    if (predicate == LIT_ALL) { return ntLen; }
    if (ntLen == 0u) { return 0u; }
    
    let hasLiteral = ((predicate >> 1u) != 0u);             // bit‑packed literal present?
    let negLit     = (predicate & $NEG_STR_LIT) != 0u;      // negative‑literal flag
    var negCount   : u32 = 0u;
    if (ntLen > 1u) { negCount = ntLen - 1u; }
    let litCount   = select(0u,
                       select(1u,                                     // positive literal ⇒ exactly 1
                               negCount,                              // negative ⇒ |Σ_A|‑1
                               negLit),
                       hasLiteral);
    return litCount;
}"""

//language=wgsl
const val ACTIVE_NT_HELPERS = """
fn activeWord(nt: u32) -> u32 { return nt >> 5u; }
fn activeMask(nt: u32) -> u32 { return 1u << (nt & 31u); }
fn activeBase(r: u32, c: u32) -> u32 { return (r * cs.numStates + c) * cs.activeWords; }
fn isActiveAt(base: u32, nt: u32) -> bool { return (atomicLoad(&active_nts[base + activeWord(nt)]) & activeMask(nt)) != 0u; }
fn setActiveAt(base: u32, nt: u32) { _ = atomicOr(&active_nts[base + activeWord(nt)], activeMask(nt)); }
"""

//language=wgsl
const val IDX_UNIFORM_STRUCT = """
struct IndexUniforms {  // Indices of all accepting states in the parse chart
    targetCnt       : atomic<u32>,  // global counter (LFSR advances on host)
    maxWordLen      : u32,
    numNonterminals : u32,
    numStates       : u32,
    threads         : u32,
    max_samples     : u32,
    
    startIdxOffset  : u32, numStartIndices : u32,
    startIndices    : array<u32> // Contains alternating (1) start index and (2) edit distance
};
"""

//language=wgsl
const val TERM_STRUCT = """
struct Terminals { // Mappings from nonterminals to terminals in CFG
    nt_tm_lens_offset : u32,    nt_tm_lens_size : u32,
       offsets_offset : u32,       offsets_size : u32,
       all_tms_offset : u32,       all_tms_size : u32,
       
       payload : array<u32>
};

$IDX_UNIFORM_STRUCT

$TM_DECODING_HELPERS
"""

//language=wgsl
const val CFL_STRUCT = """struct CFLStruct { // Carries metadata about the CFL + NFA intersection
             numStates : u32,      numNonterminals : u32,          activeWords : u32,

           mdptsOffset : u32,            mdptsSize : u32,
    mdptsOffsetsOffset : u32,     mdptsOffsetsSize : u32,
    acceptStatesOffset : u32,     acceptStatesSize : u32,
grammarFlattenedOffset : u32, grammarFlattenedSize : u32,
  grammarOffsetsOffset : u32,   grammarOffsetsSize : u32,
// Grouped closure layout:
//   leftAdj[B] = flat triples (C, parentWord, parentMask)
// where parentMask contains all A in parentWord such that A -> B C.
leftAdjFlattenedOffset : u32, leftAdjFlattenedSize : u32,
  leftAdjOffsetsOffset : u32,   leftAdjOffsetsSize : u32,

               payload : array<u32>
};

         fn getMdpt(index: u32) -> u32 { return cs.payload[cs.mdptsOffset + index]; }
   fn getMdptOffset(index: u32) -> u32 { return cs.payload[cs.mdptsOffsetsOffset + index]; }
fn getGrammarSymbol(index: u32) -> u32 { return cs.payload[cs.grammarFlattenedOffset + index]; }
fn getGrammarOffset(index: u32) -> u32 { return cs.payload[cs.grammarOffsetsOffset + index]; }

// Grouped closure layout:
//   leftAdj[B] = flat triples (C, parentWord, parentMask)
fn getLeftAdjOffset(B: u32) -> u32 { return cs.payload[cs.leftAdjOffsetsOffset + B]; }
fn getLeftAdjC(i: u32) -> u32 { return cs.payload[cs.leftAdjFlattenedOffset + i]; }
fn getLeftAdjParentWord(i: u32) -> u32 { return cs.payload[cs.leftAdjFlattenedOffset + i + 1u]; }
fn getLeftAdjParentMask(i: u32) -> u32 { return cs.payload[cs.leftAdjFlattenedOffset + i + 2u]; }
"""

//language=wgsl
const val SAT_ARTH = """
const SAT_MAX: u32 = 0xffffffffu;

fn sat_add(a: u32, b: u32) -> u32 {
  let c = a + b;
  return select(c, SAT_MAX, c < a); // overflow iff wrapped
}

fn sat_mul(a: u32, b: u32) -> u32 {
  if (a == 0u || b == 0u) { return 0u; }
  return select(a * b, SAT_MAX, a > (SAT_MAX / b));
}"""

//language=wgsl
const val WGSL_LANG_SIZE = """$SAT_ARTH
fn langSize(dpIdx: u32, numNTs: u32) -> u32 {
  let count = bp_count[dpIdx];
  if (count != 0u) { return ls_sparse[bp_offset[dpIdx] + count - 1u]; }
  return count_tms(dp_in[dpIdx], dpIdx % numNTs);
}"""

//language=text
const val PREAMBLE = """
let r = gid.x;
let c = gid.y;
if (c <= r) { return; }
let N   = cs.numStates;
let NT  = cs.numNonterminals;
let snt = N * NT;
let aoi            = r*N + c + 1u;
let pairOffset     = getMdptOffset(aoi - 1u);
var pairOffsetNext : u32;
if (aoi < cs.mdptsOffsetsSize) { pairOffsetNext = getMdptOffset(aoi); }
else { pairOffsetNext = cs.mdptsSize; }

let A = gid.z;
if (A >= NT) { return; }
let dpIdx   = r*snt + c*NT + A;
let startGC = getGrammarOffset(A);
var endGC: u32;
if (A + 1u < NT) { endGC = getGrammarOffset(A + 1u); } else { endGC = cs.grammarFlattenedSize; }
"""

const val DENSE_NT_WORKGROUP_SIZE = 64

//language=wgsl
const val WDFA_STRUCT = """
struct WDFA {
  magic       : u32,
  version     : u32,
  scale       : u32,
  numStates   : u32,
  numEdges    : u32,
  startState  : u32,
  startCost   : u32,
  missingCost : u32,

  finalCostOffset : u32, finalCostSize : u32,
  rowOffsetOffset : u32, rowOffsetSize : u32,
  edgeTokOffset   : u32, edgeTokSize   : u32,
  edgeDstOffset   : u32, edgeDstSize   : u32,
  edgeCostOffset  : u32, edgeCostSize  : u32,

  payload : array<u32>
};

const WDFA_INF : u32 = 0x3fffffffu;
const NO_EDGE  : u32 = 0xffffffffu;

fn sat_add_wdfa(a: u32, b: u32) -> u32 {
  let c = a + b;
  if (c < a || c > WDFA_INF) { return WDFA_INF; }
  return c;
}

fn final_cost(q: u32) -> u32 { return wdfa.payload[wdfa.finalCostOffset + q]; }
fn row_start(q: u32) -> u32 { return wdfa.payload[wdfa.rowOffsetOffset + q]; }
fn row_end(q: u32) -> u32 { return wdfa.payload[wdfa.rowOffsetOffset + q + 1u]; }
fn edge_tok(e: u32) -> u32 { return wdfa.payload[wdfa.edgeTokOffset + e]; }
fn edge_dst(e: u32) -> u32 { return wdfa.payload[wdfa.edgeDstOffset + e]; }
fn edge_cost(e: u32) -> u32 { return wdfa.payload[wdfa.edgeCostOffset + e]; }

// Rows are sorted by token id on the Kotlin side.
fn find_edge(q: u32, tok: u32) -> u32 {
  var lo = row_start(q);
  var hi = row_end(q);

  while (lo < hi) {
    let mid = (lo + hi) >> 1u;
    let t = edge_tok(mid);

    if (t == tok) { return mid; }
    if (tok < t) { hi = mid; }
    else { lo = mid + 1u; }
  }

  return NO_EDGE;
}"""

//language=wgsl
const val CHART_INIT_PREAMBLE = """$CFL_STRUCT $TERM_STRUCT $ACTIVE_NT_HELPERS
@group(0) @binding(0) var<storage, read_write>         dp_in : array<u32>;
@group(0) @binding(1) var<storage, read_write>    active_nts : array<atomic<u32>>;
@group(0) @binding(2) var<storage, read>              word : array<u32>;
@group(0) @binding(3) var<storage, read>                cs : CFLStruct;
@group(0) @binding(4) var<storage, read>         terminals : Terminals;

fn letter_at(idx : u32, wd_len : u32) -> u32 { return select(word[idx], 0xffffffffu, idx >= wd_len); }

fn literal_index(A_nt_idx : u32, sigma_token : u32) -> u32 {
    if (sigma_token == 0xffffffffu) { return 0u; }
    let ntLen = get_nt_tm_lens(A_nt_idx);
    let ntOff = get_offsets(A_nt_idx);
    for (var k : u32 = 0u; k < ntLen; k = k + 1u) {
        if (get_all_tms(ntOff + k) == sigma_token) { return k + 1u; }
    }
    return 0u;
}

fn encode_pos_literal(A_nt_idx : u32, sigma_token : u32) -> u32 {
    return literal_index(A_nt_idx, sigma_token) << 1u;
}"""

//language=wgsl
val init_lev_chart by Shader("""$CHART_INIT_PREAMBLE

fn pack_rc(row_j: u32, col_i: u32) -> u32 { return (row_j << 16u) | (col_i & 0xffffu); }
fn unpack_row_j(packed: u32) -> u32 { return packed >> 16u; }
fn unpack_col_i(packed: u32) -> u32 { return packed & 0xffffu; }

fn tpl(x: i32) -> i32 { let y: i32 = max(x, 0); return (y * (y + 1)) / 2; }

fn prefix_count(s_sum: i32, num_j_values: i32, num_i_values: i32) -> i32 {
    return tpl(s_sum) - tpl(s_sum - num_j_values) - tpl(s_sum - num_i_values) + tpl(s_sum - num_j_values - num_i_values);
}

fn find_target_sum(rank_k: u32, max_j_idx: u32, max_i_idx: u32) -> i32 {
    var target_s: i32 = 0;
    var low: i32 = 0;
    var high: i32 = i32(max_j_idx + max_i_idx);
    let num_j_vals = i32(max_j_idx + 1u);
    let num_i_vals = i32(max_i_idx + 1u);

    while (low <= high) {
        let mid_s = low + (high - low) / 2;
        if (prefix_count(mid_s, num_j_vals, num_i_vals) <= i32(rank_k)) {
            target_s = mid_s;
            low = mid_s + 1;
        } else {
            high = mid_s - 1;
        }
    }
    return target_s;
}

// unrank_to_coords(rank_idx, max_j_idx, max_i_idx) -> packed_coords(j,i)
// Primary sort: s = i+j (ascending)
// Secondary sort: i (string position, ascending). For fixed s, as i increases, j decreases.
fn unrank_to_coords(rank_idx: u32, max_j_idx: u32, max_i_idx: u32) -> u32 {
    let num_j_vals = i32(max_j_idx + 1u);
    let num_i_vals = i32(max_i_idx + 1u);

    let s_sum: i32 = find_target_sum(rank_idx, max_j_idx, max_i_idx);
    let elements_before_this_sum_group: i32 = prefix_count(s_sum, num_j_vals, num_i_vals);
    let offset_in_sum_group: i32 = i32(rank_idx) - elements_before_this_sum_group;

    let i_start_for_sum: i32 = max(0, s_sum - i32(max_j_idx));
    let i_final: u32 = u32(i_start_for_sum + offset_in_sum_group);
    let j_final: u32 = u32(s_sum - i32(i_final));

    return pack_rc(j_final, i_final);
}

fn encode_neg_literal(A_nt_idx : u32, sigma_token : u32) -> u32 {
    let index = literal_index(A_nt_idx, sigma_token);
    if (index == 0u) { return LIT_ALL; }
    return NEG_BIT | (index << 1u);
}

const MAX_J_IDX_CONST : u32 = ${MAX_LEV_RAD}u; // Max index for j (edit distance)

@compute @workgroup_size(1,1,$DENSE_NT_WORKGROUP_SIZE) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    let q1_rank = gid.x;
    let q2_rank = gid.y;
    let A_idx   = gid.z;
    let nts     = cs.numNonterminals;
    if (A_idx >= nts) { return; }

    let dpIdx = q1_rank * cs.numStates * nts + q2_rank * nts + A_idx;

    let current_word_len = arrayLength(&word); // Max i is current_word_len (0 to N for string of length N)

    let packed_q1_ji = unrank_to_coords(q1_rank, MAX_J_IDX_CONST, current_word_len);
    let j1 = unpack_row_j(packed_q1_ji);
    let i1 = unpack_col_i(packed_q1_ji);

    let packed_q2_ji = unrank_to_coords(q2_rank, MAX_J_IDX_CONST, current_word_len);
    let j2 = unpack_row_j(packed_q2_ji);
    let i2 = unpack_col_i(packed_q2_ji);

    let di = i32(i2) - i32(i1);
    let dj = i32(j2) - i32(j1);

    var encoded_predicate_val : u32 = 0u;
    var encoded_edit_val : u32 = 0u;
    var should_write_to_dp_in : bool = false;
    let num_prods_for_A = get_nt_tm_lens(A_idx);
    
    // (1) UP ARC (Insertion): q1=(i,j-1) -> q2=(i,j). Predicate: [!=word[i1]]
    if (di == 0 && dj == 1) {
        if (j1 < MAX_J_IDX_CONST && i1 <= current_word_len) {
            let s_char = letter_at(i1, current_word_len);
            encoded_predicate_val = encode_neg_literal(A_idx, s_char);
            if (encoded_predicate_val == LIT_ALL) { should_write_to_dp_in = (num_prods_for_A > 0u); }
            else if ((encoded_predicate_val & NEG_BIT) != 0u) { should_write_to_dp_in = (num_prods_for_A > 1u); }
            encoded_edit_val = EDIT_INSERT_CODE << EDIT_DEL_SHIFT;
        }
    }
    // (2) RIGHT ARC (Match): q1=(i-1,j) -> q2=(i,j). Predicate: word[i1]
    else if (di == 1 && dj == 0) {
        if (i1 < current_word_len) {
            let s_char = letter_at(i1, current_word_len);
            encoded_predicate_val = encode_pos_literal(A_idx, s_char);
            if (encoded_predicate_val != 0u) { should_write_to_dp_in = true; }
        }
    }
    // (3) DIAG ARC (Substitution): q1=(i-1,j-1) -> q2=(i,j). Predicate: [!=word[i1]]
    else if (di == 1 && dj == 1) {
        if (i1 < current_word_len && j1 < MAX_J_IDX_CONST) {
            let s_char = letter_at(i1, current_word_len);
            encoded_predicate_val = encode_neg_literal(A_idx, s_char);
            if (encoded_predicate_val == LIT_ALL) { should_write_to_dp_in = (num_prods_for_A > 0u); }
            else if ((encoded_predicate_val & NEG_BIT) != 0u) { should_write_to_dp_in = (num_prods_for_A > 1u); }
            encoded_edit_val = EDIT_SUB_BIT;
        }
    }
    // (4) "KNIGHT" ARC (Deletion): q1=(i,j) -> q2=(i+d+1,j+d). Predicate: word[i1+d]
    else if (dj >= 1 && di == dj + 1) {
        let d_val = u32(dj);
        if (i1 + d_val < current_word_len) {
            if ((i1 + d_val + 1u <= current_word_len) && (j1 + d_val <= MAX_J_IDX_CONST)) {
                let s_char = letter_at(i1 + d_val, current_word_len);
                encoded_predicate_val = encode_pos_literal(A_idx, s_char);
                if (encoded_predicate_val != 0u) { should_write_to_dp_in = true; }
                encoded_edit_val = d_val << EDIT_DEL_SHIFT;
            }
        }
    }

    if (should_write_to_dp_in) {
        dp_in[dpIdx] = encoded_predicate_val | encoded_edit_val;
        setActiveAt(activeBase(q1_rank, q2_rank), A_idx);
    }
}""")

//language=wgsl
val init_line_chart by Shader("""$CHART_INIT_PREAMBLE

@compute @workgroup_size(1,1,$DENSE_NT_WORKGROUP_SIZE) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    let q1 = gid.x;
    let q2 = gid.y;
    let A  = gid.z;
    let nts = cs.numNonterminals;
    if (A >= nts) { return; }

    if (q2 != q1 + 1u) { return; } // only adjacent edges in the chain

    let dpIdx = q1 * cs.numStates * nts + q2 * nts + A;

    let wd_len = arrayLength(&word);
    let tok    = letter_at(q1, wd_len);

    let ntLen = get_nt_tm_lens(A);

    // Hole => allow any terminal in Σ_A (wildcard)
    if (tok == 0xffffffffu) {
        if (ntLen > 0u) { dp_in[dpIdx] = LIT_ALL; setActiveAt(activeBase(q1, q2), A); }
        return;
    }

    // Fixed token => only that literal
    let enc = encode_pos_literal(A, tok);
    if (enc != 0u) { dp_in[dpIdx] = enc; setActiveAt(activeBase(q1, q2), A); }
}""")

//language=wgsl
val dag_reach by Shader("""struct AtomicChange { count: atomic<u32> };
@group(0) @binding(0) var<storage, read_write>   input : array<u32>;
@group(0) @binding(1) var<storage, read_write> changes : AtomicChange;

@compute @workgroup_size(1,1,1) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    let x = gid.x;
    let y = gid.y;
    if (x >= y) { return; }
    let width = u32(sqrt(f32(arrayLength(&input))));
//    if (x == y) { input[x * width + y] = 1u; atomicAdd(&changes.count, 1u); return; }
    if (input[x * width + y] == 1u) { atomicAdd(&changes.count, 1u); return; }

    for (var k = 0u; k < width; k = k + 1u) {
        if (input[x * width + k] == 1u && input[k * width + y] == 1u) {
            input[x * width + y] = 1u;
            atomicAdd(&changes.count, 1u);
            return;
        }
    }
}""")

//language=wgsl
val mdpt_count by Shader("""struct Uni { n : u32 };
@group(0) @binding(0) var<storage, read>        reach : array<u32>;   // N×N upper‑tri (0/1)
@group(0) @binding(1) var<storage, read_write> counts : array<u32>;   // N×N (aoi‑1 → #midpts)
@group(0) @binding(2) var<uniform>                uni : Uni;

@compute @workgroup_size(1,1,1) fn main(@builtin(global_invocation_id) gid:vec3<u32>) {
    let r = gid.y;  let c = gid.x;  let N = uni.n;
    if (r >= N || c >= N || c <= r) { return; }

    let idx = r*N + c;
    if (reach[idx]==0u) { counts[idx]=0u; return; }

    var cnt = 0u;
    for (var v=0u; v<N; v++) { if (reach[r*N+v]==1u && reach[v*N+c]==1u) { cnt++; } }
    counts[idx] = cnt;
}""")

//language=wgsl
val mdpt_write by Shader("""struct Uni { n : u32 };
@group(0) @binding(0) var<storage, read>         reach : array<u32>;
@group(0) @binding(1) var<storage, read>       offsets : array<u32>; // exclusive scan of counts
@group(0) @binding(2) var<storage, read_write> flat_mp : array<u32>; // flattened mid‑points
@group(0) @binding(3) var<uniform>                 uni : Uni;

@compute @workgroup_size(1,1,1) fn main(@builtin(global_invocation_id) gid:vec3<u32>) {
    let r = gid.y;  let c = gid.x;  let N = uni.n;
    if (r >= N || c >= N || c <= r) { return; }

    let idx = r*N + c;
    if (reach[idx]==0u) { return; }

    var out = offsets[idx];
    for (var v=0u; v<N; v++) { if (reach[r*N+v]==1u && reach[v*N+c]==1u) { flat_mp[out] = v; out++; } }
}""")

const val CFL_WORKGROUP_SIZE_X = 16
const val CFL_WORKGROUP_SIZE_Y = 4

//language=wgsl
val cfl_mul_upper by Shader("""$CFL_STRUCT $ACTIVE_NT_HELPERS
struct IterationState { count: atomic<u32>, round: u32 };

@group(0) @binding(0) var<storage, read_write>         dp_in : array<u32>;
@group(0) @binding(1) var<storage, read_write>    active_nts : array<atomic<u32>>;
@group(0) @binding(2) var<storage, read>                cs : CFLStruct;
@group(0) @binding(3) var<storage, read_write>          iter : IterationState;
@group(0) @binding(4) var<storage, read_write> changed_cells : array<atomic<u32>>;
@group(0) @binding(5) var<storage, read_write>    binary_nts : array<u32>;

fn flushParentMask(r: u32, c: u32, aw: u32, mask: u32, tgtBase: u32, snt: u32, NT: u32) -> u32 {
    if (mask == 0u || aw >= cs.activeWords) { return 0u; }

    let binaryIdx = tgtBase + aw;
    let oldBinary = binary_nts[binaryIdx];
    let newBinary = mask & ~oldBinary;
    if (newBinary == 0u) { return 0u; }
    binary_nts[binaryIdx] = oldBinary | newBinary;

    let oldActive = atomicLoad(&active_nts[tgtBase + aw]);
    let newlyActive = newBinary & ~oldActive;

    if (newlyActive != 0u) { _ = atomicOr(&active_nts[tgtBase + aw], newlyActive); }

    var localChanges = 0u;
    // Write all new binary support, but count only entries that become active.
    var pending = newBinary;
    loop {
        if (pending == 0u) { break; }

        let bit = firstTrailingBit(pending);
        pending = pending & (pending - 1u);

        if (bit == 0xffffffffu) { break; }

        let A = (aw << 5u) + bit;
        if (A >= NT) { continue; }

        let dpIdx = r * snt + c * NT + A;
        dp_in[dpIdx] = dp_in[dpIdx] | 1u;
        if ((newlyActive & (1u << bit)) != 0u) {
            localChanges = localChanges + 1u;
        }
    }

    return localChanges;
}

@compute @workgroup_size($CFL_WORKGROUP_SIZE_X,$CFL_WORKGROUP_SIZE_Y,1) 
fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    let r = gid.y;
    let c = gid.x;
    let N = cs.numStates;
    if (r >= N || c >= N || c <= r) { return; }

    let NT = cs.numNonterminals;
    let snt = N * NT;
    let aoi = r * N + c + 1u;
    let pairOffset = getMdptOffset(aoi - 1u);
    var pairOffsetNext: u32;
    if (aoi < cs.mdptsOffsetsSize) { pairOffsetNext = getMdptOffset(aoi); }
    else { pairOffsetNext = cs.mdptsSize; }

    let tgtBase = activeBase(r, c);
    var localChanges = 0u;

    for (var pairIdx = pairOffset; pairIdx < pairOffsetNext; pairIdx = pairIdx + 1u) {
        let m = getMdpt(pairIdx);

        let leftBase  = activeBase(r, m);
        let rightBase = activeBase(m, c);

        if (iter.round != 0u) {
            let leftChanged  = atomicLoad(&changed_cells[r * N + m]);
            let rightChanged = atomicLoad(&changed_cells[m * N + c]);
            if (leftChanged != iter.round && rightChanged != iter.round) { continue; }
        }

        for (var w = 0u; w < cs.activeWords; w = w + 1u) {
            var leftBits = atomicLoad(&active_nts[leftBase + w]);

            loop {
                if (leftBits == 0u) { break; }

                let bit = firstTrailingBit(leftBits);
                leftBits = leftBits & (leftBits - 1u);

                if (bit == 0xffffffffu) { break; }

                let B = (w << 5u) + bit;
                if (B >= NT) { continue; }

                let startLA = getLeftAdjOffset(B);
                let endLA = getLeftAdjOffset(B + 1u);

                // grouped triples: (C, parentWord, parentMask)
                for (var g = startLA; g < endLA; g = g + 3u) {
                    let C = getLeftAdjC(g);

                    if (!isActiveAt(rightBase, C)) { continue; }

                    let aw = getLeftAdjParentWord(g);
                    let pm = getLeftAdjParentMask(g);

                    localChanges = localChanges + flushParentMask(r, c, aw, pm, tgtBase, snt, NT);
                }
            }
        }
    }

    if (localChanges != 0u) {
        atomicStore(&changed_cells[r * N + c], iter.round + 1u);
        atomicAdd(&iter.count, localChanges);
    }
}""")

//language=wgsl
val bp_count by Shader("""$CFL_STRUCT
@group(0) @binding(0) var<storage, read>           dp_in : array<u32>;
@group(0) @binding(1) var<storage, read_write>  bp_count : array<u32>;
@group(0) @binding(2) var<storage, read>              cs : CFLStruct;

@compute @workgroup_size(1,1,$DENSE_NT_WORKGROUP_SIZE) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    $PREAMBLE
    
    if ((dp_in[dpIdx] & 0x01u) == 0u) { bp_count[dpIdx] = 0; return; }
    
    var count = 0u;
    for (var pairIdx = pairOffset; pairIdx < pairOffsetNext; pairIdx++) {
        let mdpt = getMdpt(pairIdx); for (var g = startGC; g < endGC; g+= 2u) {
            let B = getGrammarSymbol(g);
            let C = getGrammarSymbol(g + 1u);

            let idxBM = r*snt + mdpt*NT + B;
            let idxMC = mdpt*snt + c*NT + C;

            if (dp_in[idxBM] != 0u && dp_in[idxMC] != 0u) { count++; }
        }
    }

    bp_count[dpIdx] = count;
}""")

//language=wgsl
val bp_write by Shader("""$CFL_STRUCT
@group(0) @binding(0) var<storage, read>             dp_in : array<u32>;
@group(0) @binding(1) var<storage, read_write>   bp_offset : array<u32>;
@group(0) @binding(2) var<storage, read_write>  bp_storage : array<u32>;
@group(0) @binding(3) var<storage, read>                cs : CFLStruct;

@compute @workgroup_size(1,1,$DENSE_NT_WORKGROUP_SIZE) fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
    $PREAMBLE
    
    if ((dp_in[dpIdx] & 0x01u) == 0u) { return; }

    var outPos = bp_offset[dpIdx];

    for (var pairIdx = pairOffset; pairIdx < pairOffsetNext; pairIdx = pairIdx + 1u) {
        let mdpt = getMdpt(pairIdx); for (var g = startGC; g < endGC; g += 2u) {
            let B = getGrammarSymbol(g);
            let C = getGrammarSymbol(g + 1u);

            let idxBM = r*snt + mdpt*NT + B;
            let idxMC = mdpt*snt + c*NT + C;

            if (dp_in[idxBM] != 0u && dp_in[idxMC] != 0u) {
                bp_storage[outPos * 2u + 0u] = idxBM;
                bp_storage[outPos * 2u + 1u] = idxMC;
                outPos++;
            }
        }
    }
}""")

//language=wgsl
val ls_cdf by Shader("""$CFL_STRUCT $TERM_STRUCT
struct SpanUni { span : u32 };
@group(0) @binding(0) var<storage, read>           dp_in : array<u32>;
@group(0) @binding(1) var<storage, read>              cs : CFLStruct;
@group(0) @binding(2) var<storage, read>       terminals : Terminals;
@group(0) @binding(3) var<uniform>                    su : SpanUni;
@group(0) @binding(4) var<storage, read>        bp_count : array<u32>;
@group(0) @binding(5) var<storage, read>       bp_offset : array<u32>;
@group(0) @binding(6) var<storage, read>      bp_storage : array<u32>;
@group(0) @binding(7) var<storage, read_write> ls_sparse : array<u32>;

$WGSL_LANG_SIZE

@compute @workgroup_size(1,1,1) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    let r = gid.x;
    let c = r + su.span;
    if (c >= cs.numStates) { return; }
    let A = gid.z;

    let N     = cs.numStates;
    let NT    = cs.numNonterminals;
    let dpIdx = (r * N + c) * NT + A;
    
    let val = dp_in[dpIdx];
    if (val == 0u) { return; }
    if ((val & 0x01u) == 0u) { return; }

    var total = count_tms(val, A);

    let base = bp_offset[dpIdx];
    let count = bp_count[dpIdx];
    for (var i = 0u; i < count; i = i + 1u) {
        let pairBase = (base + i) * 2u;
        let left = bp_storage[pairBase];
        let right = bp_storage[pairBase + 1u];
        let leftSize = max(langSize(left, NT), 1u);
        let rightSize = max(langSize(right, NT), 1u);
        total = sat_add(total, sat_mul(leftSize, rightSize));
        ls_sparse[base + i] = total;
    }
}""")

const val PFX_SUM_PARAMS = """struct PrefixSumUni { n : u32, numBlocks : u32, threads : u32 };"""
// language=wgsl
val prefix_sum_p1 by Shader("""$PFX_SUM_PARAMS $SAT_ARTH
@group(0) @binding(0) var<storage, read>         inputBuf : array<u32>;
@group(0) @binding(1) var<storage, read_write>  outputBuf : array<u32>;
@group(0) @binding(2) var<storage, read_write>  blockSums : array<u32>;
@group(0) @binding(3) var<uniform>              prefixUni : PrefixSumUni;

const WORKGROUP_SIZE: u32 = 256u;

var<workgroup> tile: array<u32, WORKGROUP_SIZE>;

@compute @workgroup_size(WORKGROUP_SIZE) fn main(
  @builtin(workgroup_id)        groupId : vec3<u32>,
  @builtin(local_invocation_id) localId : vec3<u32>
) {
    let N     = prefixUni.n;
    let block = groupId.x + groupId.y * prefixUni.threads;
    
    if (block >= prefixUni.numBlocks) { return; }
    
    let lid = localId.x;
    let gid = block * WORKGROUP_SIZE + lid;   // global element index

    // 1) Load data from inputBuf into shared workgroup array `tile`.
    if (gid < N) { tile[lid] = inputBuf[gid]; } else { tile[lid] = 0u; }
    workgroupBarrier();

    // 2) Up-sweep: build partial sums in place.
    //    Offsets go 1, 2, 4, 8, ...
    var offset = 1u;
    while (offset < WORKGROUP_SIZE) {
        // index = (lid+1)*offset*2 - 1
        let idx = ((lid + 1u) * offset * 2u) - 1u;
        if (idx < WORKGROUP_SIZE) { tile[idx] = sat_add(tile[idx], tile[idx - offset]); }
        workgroupBarrier();
        offset = offset * 2u;
    }

    // 3) The last element of `tile` now has the total sum of this block.
    //    Save that to blockSums, then zero it out so this becomes an EXCLUSIVE scan.
    if (lid == 0u) {
        blockSums[block] = tile[WORKGROUP_SIZE - 1u];
        tile[WORKGROUP_SIZE - 1u] = 0u;
    }
    workgroupBarrier();

    // 4) Down-sweep: push each partial sum back down the tree to build the exclusive scan.
    //    Offsets go (256 >> 1), (256 >> 2), ...
    offset = WORKGROUP_SIZE / 2u;
    while (offset > 0u) {
        let idx = ((lid + 1u) * offset * 2u) - 1u;
        if (idx < WORKGROUP_SIZE) {
            let tmp = tile[idx - offset];
            tile[idx - offset] = tile[idx];
            tile[idx] = sat_add(tile[idx], tmp);
        }
        workgroupBarrier();
        offset = offset / 2u;
    }

    // 5) Write the per-element results back out to outputBuf.
    if (gid < N) { outputBuf[gid] = tile[lid]; }
}""")

//language=wgsl
val prefix_sum_p2 by Shader("""$PFX_SUM_PARAMS $SAT_ARTH
@group(0) @binding(0) var<storage, read_write>          dataBuf : array<u32>;
@group(0) @binding(1) var<storage, read>       scannedBlockSums : array<u32>;
@group(0) @binding(2) var<uniform>                    prefixUni : PrefixSumUni;

@compute @workgroup_size(256) fn main(
    @builtin(workgroup_id)        groupId : vec3<u32>,
    @builtin(local_invocation_id) localId : vec3<u32>
) {
    let block = groupId.x + groupId.y * prefixUni.threads;
    if (block >= prefixUni.numBlocks) { return; }

    let gid = block * 256u + localId.x;
    if (gid >= prefixUni.n) { return; }

    let offset = scannedBlockSums[block];
    dataBuf[gid] = sat_add(dataBuf[gid], offset);
}""")

// Longest word WGSL can handle. If ~2^9<MAX_WORD_LEN, pipeline breaks some on architectures
const val MAX_WORD_LEN = 128
const val MAX_SAMPLES = 300_000 // Maximum number of samples to draw before reranking
const val MAX_DISP_RESULTS = 29
const val TOP_K_SAMP = 10 * MAX_DISP_RESULTS // Maximum results to sample, some of which may be displayed to the user
const val RERANKER_TOP_K_SAMP = 1_000
const val DISPATCH_GROUP_SIZE_X = 65_535
// Length of the packet header in each repair buffer
const val PKT_HDR_LEN = 2 // [edit distance, PCFG log-cost (then combined decoder rank cost)]
const val PACKED_EDIT_SHIFT = 24
const val PACKED_TOKEN_LIMIT = 1 shl PACKED_EDIT_SHIFT
const val PACKED_TOKEN_MASK = PACKED_TOKEN_LIMIT - 1
const val PACKED_INSERTION_TAG = 1
const val PACKED_SUBSTITUTION_TAG = 2
const val PACKED_FIRST_DELETION_TAG = 3
const val PACKED_LAST_DELETION_TAG = PACKED_SUBSTITUTION_TAG + MAX_LEV_RAD
const val SENTINEL = 0xFFFF_FFFFu
const val HASH_MUL = 0x1e35a7bdu

//language=wgsl
val build_root_sizes by Shader("""$TERM_STRUCT
@group(0) @binding(0) var<storage, read>        dp_in      : array<u32>;
@group(0) @binding(1) var<storage, read>        bp_count   : array<u32>;
@group(0) @binding(2) var<storage, read>        bp_offset  : array<u32>;
@group(0) @binding(3) var<storage, read>        ls_sparse  : array<u32>;
@group(0) @binding(4) var<storage, read>        terminals  : Terminals;
@group(0) @binding(5) var<storage, read_write>  root_sizes : array<u32>;
@group(0) @binding(6) var<storage, read_write>  idx_uni    : IndexUniforms;

$CHART_DECODING_HELPERS

$WGSL_LANG_SIZE

@compute @workgroup_size(256) fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  let numRoots = idx_uni.numStartIndices / 2u;
  if (i >= numRoots) { return; }
  root_sizes[i] = langSize(getStartIdx(i), idx_uni.numNonterminals);
}""")

//language=wgsl
const val WGSL_MIX32 = """fn mix32(x: u32) -> u32 {
  var z = x + 0x9e3779b9u;
  z = (z ^ (z >> 16u)) * 0x85ebca6bu;
  z = (z ^ (z >> 13u)) * 0xc2b2ae35u;
  return z ^ (z >> 16u);
}
"""

//language=wgsl
internal const val PACK_EDIT_TOKEN_HELPER = """
fn packEditToken(token: u32, val: u32) -> u32 {
  let editCode = (val & EDIT_DEL_MASK) >> EDIT_DEL_SHIFT;
  var editTag = 0u;
  if (editCode == EDIT_INSERT_CODE) { editTag = ${PACKED_INSERTION_TAG}u; }
  else if (editCode != 0u) { editTag = editCode + ${PACKED_SUBSTITUTION_TAG}u; }
  else if ((val & EDIT_SUB_BIT) != 0u) { editTag = ${PACKED_SUBSTITUTION_TAG}u; }
  return (token & ${PACKED_TOKEN_MASK}u) | (editTag << ${PACKED_EDIT_SHIFT}u);
}
"""

//language=wgsl
const val SAMPLER_PARAMS = """struct Params { maxSamples: u32, k: u32, stride: u32, threads: u32 };"""
//language=wgsl
val markov_score by Shader("""$SAMPLER_PARAMS
$SAT_ARTH
@group(0) @binding(0) var<storage, read_write>  packets : array<u32>; // sampledWords/outBuf
@group(0) @binding(1) var<storage, read>          ngram : array<u32>; // hash table
@group(0) @binding(2) var<uniform>                  prm : Params;

const PKT_HDR_LEN  : u32 = ${PKT_HDR_LEN}u;
const TOKEN_MASK   : u32 = ${PACKED_TOKEN_MASK}u;
const SENTINEL_KEY : u32 = ${SENTINEL.toHexString(hexFmt)};
const HASH_MUL     : u32 = ${HASH_MUL.toHexString(hexFmt)};      // same multiplier as CPU side
const BOS_ID       : u32 = ${BOS_ID}u;
const NEWLINE_ID   : u32 = ${NEWLINE_ID}u;
const EOS_ID       : u32 = ${EOS_ID}u;

fn packGram(a : u32, b : u32, c : u32,d : u32) -> u32 { return (a<<21u)|(b<<14u)|(c<<7u)|d; }

fn hash32(x : u32, pow : u32) -> u32 { return (x * HASH_MUL) >> (32u - pow); }

fn lookupScore(key: u32) -> u32 {
    let pow   : u32 = ngram[0];
    let mask  : u32 = (1u << pow) - 1u;
    var slot  : u32 = hash32(key, pow) & mask;

    loop {                                       // ≤ 8 probes when load ≤ 0.75
        let idx      = 1u + slot * 2u;           // 1-word header → slot*2
        let stored   = ngram[idx];
        if (stored == key)          { return ngram[idx + 1u]; } // hit
        if (stored == SENTINEL_KEY) { return 1u; }              // empty
        slot = (slot + 1u) & mask;                              // linear probe
    }
}

@compute @workgroup_size(1,1,1) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
    let sid = gid.x + gid.y * prm.threads;
    if (sid >= prm.maxSamples) { return; }

    let stride : u32 = prm.stride;
    let base   : u32 = sid * stride;

    var t0 : u32 = BOS_ID     - 1u;
    var t1 : u32 = NEWLINE_ID - 1u;

    // -- pre-fetch the guaranteed first real token -----------
    var pos : u32 = 1u;
    let w1  : u32 = packets[base + PKT_HDR_LEN] & TOKEN_MASK;
    var t2  : u32 = w1 - 1u;

    var score       : u32 = packets[base + 1u]; // -log(P_CFG * P_ngram) = -log(P_CFG) - log(P_ngram)
    var doneSuffix  : u32 = 0u;

    loop {
        // ----- next token or synthetic suffix ---------------
        var tok : u32;
        if (pos < stride - PKT_HDR_LEN && packets[base + PKT_HDR_LEN + pos] != 0u) {
            tok = packets[base + PKT_HDR_LEN + pos] & TOKEN_MASK;
            pos += 1u;
        } else {
            // two‑token suffix: NEWLINE , EOS
            tok = select(EOS_ID, NEWLINE_ID, doneSuffix == 0u);
            doneSuffix += 1u;
            if (doneSuffix > 2u) { break; }
        }

        // ----- accumulate n-gram score ----------------------
        let key = packGram(t0, t1, t2, tok - 1u);
        score = sat_add(score, lookupScore(key));

        t0 = t1; t1 = t2; t2 = tok - 1u;
    }

    packets[base + 1u] = sat_add(score, (packets[base] + 1u) * 10000000u);
}""")

//language=wgsl
val wdfa_score by Shader("""$SAMPLER_PARAMS
$WDFA_STRUCT

@group(0) @binding(0) var<storage, read_write> packets : array<u32>;
@group(0) @binding(1) var<storage, read>       wdfa    : WDFA;
@group(0) @binding(2) var<uniform>             prm     : Params;

const PKT_HDR_LEN : u32 = ${PKT_HDR_LEN}u;
const TOKEN_MASK  : u32 = ${PACKED_TOKEN_MASK}u;
const EDIT_COST_STRIDE : u32 = 10000000u;
const WDFA_SCORE_INVALID : u32 = 0xffffffffu;

@compute @workgroup_size(1,1,1) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {
  let sid = gid.x + gid.y * prm.threads;
  if (sid >= prm.maxSamples) { return; }

  let stride = prm.stride;
  let base = sid * stride;

  if (packets[base + 1u] == WDFA_SCORE_INVALID) { return; }
  var q = wdfa.startState;
  // Convert cached PCFG log-cost to the WDFA's scale before multiplying probabilities in log space.
  let pcfgCost = u32(min(round(f32(packets[base + 1u]) * (f32(wdfa.scale) / $SCALE)), f32(WDFA_INF)));
  var cost = sat_add_wdfa(wdfa.startCost, pcfgCost);

  var pos : u32 = 0u;
  loop {
    if (pos >= stride - PKT_HDR_LEN) { break; }

    let packedTok = packets[base + PKT_HDR_LEN + pos];
    if (packedTok == 0u) { break; }
    let tok = packedTok & TOKEN_MASK;

    let e = find_edge(q, tok);
    if (e == NO_EDGE) {
      cost = sat_add_wdfa(cost, wdfa.missingCost);
    } else {
      cost = sat_add_wdfa(cost, edge_cost(e));
      q = edge_dst(e);
    }

    pos = pos + 1u;
  }

  let fc = final_cost(q);
  if (fc >= WDFA_INF) {
    packets[base + 1u] = WDFA_SCORE_INVALID;
    return;
  }
  cost = sat_add_wdfa(cost, fc);
  if (cost >= WDFA_INF) {
    packets[base + 1u] = WDFA_SCORE_INVALID;
    return;
  }

  // Preserve the current edit-distance-first ranking convention.
  packets[base + 1u] = sat_add_wdfa(cost, (packets[base] + 1u) * EDIT_COST_STRIDE);
}""")

//language=wgsl
val sparse_mat_load by Shader("""struct SparseElement { r: u32, c: u32 };
@group(0) @binding(0) var<storage, read>     sparse_elements : array<SparseElement>;
@group(0) @binding(1) var<storage, read_write> output_buffer : array<u32>;

const WORKGROUP_SIZE: u32 = ${SPARSE_WRITER_WORKGROUP_SIZE}u;

@compute @workgroup_size(WORKGROUP_SIZE) fn main(@builtin(global_invocation_id) global_id: vec3<u32>) {
    let index = global_id.x;
    let num_elements = arrayLength(&sparse_elements);
    let output_size = arrayLength(&output_buffer);
    if (index >= num_elements) { return; }
    let element = sparse_elements[index];
    let width = u32(sqrt(f32(output_size)));
    let target_index = element.r * width + element.c;
    if (target_index < output_size) { output_buffer[target_index] = 1; }
}""")

const val SPARSE_WRITER_WORKGROUP_SIZE = 256

object GPUBufferUsage {
  const val MAP_READ      = 0x0001
  const val COPY_SRC      = 0x0004
  const val COPY_DST      = 0x0008
  const val UNIFORM       = 0x0040
  const val STORAGE       = 0x0080
  const val STCPSD = STORAGE or COPY_SRC or COPY_DST
}

class Shader constructor(val src: String) {
  lateinit var name: String
  lateinit var pipeline: GPUComputePipeline

  @OptIn(ExperimentalWasmJsInterop::class)
  suspend fun bind() = gpu.createComputePipelineAsync(
    GPUComputePipelineDescriptor(
      layout = "auto",
      compute = GPUProgrammableStage(
        module = gpu.createShaderModule(GPUShaderModuleDescriptor(label = name, code = src)),
        entryPoint = "main"
      )
    )
  ).unsafeCast<KPromise<GPUComputePipeline>>().await().also { pipeline = it }

  operator fun getValue(tr: Any?, property: KProperty<*>): Shader = this.also { name = property.name }

  companion object {
    fun GPUComputePipeline.bindBuffers(label: String, vararg buffers: GPUBuffer): GPUBindGroup {
      inline fun <T> jsObject(init: dynamic.() -> Unit): T { val o = js("{}"); init(o); return o as T }
      val ent = buffers.mapIndexed { index, buf ->
        GPUBindGroupEntry(binding = index, resource = jsObject { buffer = buf })
      }.toTypedArray()
      return gpu.createBindGroup(GPUBindGroupDescriptor(label = label, layout = getBindGroupLayout(0), entries = ent))
    }

    fun GPUBuffer.writeU32(wordIndex: Int, value: Int) {
      val tmp = JSIntArray(1).apply { set(arrayOf(value), 0) }
      gpu.queue.writeBuffer(this, (wordIndex * 4).toDouble(), tmp)
    }

    suspend fun GPUBuffer.readIndices(indices: List<Int>): Int32Array<ArrayBuffer> {
      val t0 = TimeSource.Monotonic.markNow()
      val stagingBuffer = GPUBuffer(indices.size * 4L, GPUBufferUsage.COPY_DST or GPUBufferUsage.MAP_READ)
      val encoder = gpu.createCommandEncoder()
      indices.forEachIndexed { i, idx ->
        encoder.copyBufferToBuffer(
          source = this,
          sourceOffset = idx.toDouble() * 4,
          destination = stagingBuffer,
          destinationOffset = i.toDouble() * 4,
          size = 4.0
        )
      }
      gpu.queue.submit(arrayOf(encoder.finish()))
      stagingBuffer.mapAsync(GPUMapMode.READ).unsafeCast<Promise<*>>().await()
      val result = Int32Array(stagingBuffer.getMappedRange()).slice()
      stagingBuffer.destroy()
      log("Read ${indices.size}/${size.toInt()} bytes in ${t0.elapsedNow()}")
      return result
    }

    fun createParseChart(usage: Int, totalSizeInInts: Int): GPUBuffer {
      val outputByteSize = totalSizeInInts.toLong() * Int32Array.BYTES_PER_ELEMENT
      return GPUBuffer(outputByteSize, usage or GPUBufferUsage.STORAGE or GPUBufferUsage.COPY_DST)
    }

    fun IntArray.toSquareMatrixSparse(n: Int): GPUBuffer {
      val outputByteSize = n * n * Int32Array.BYTES_PER_ELEMENT
      val outputBuffer = GPUBuffer(outputByteSize, STCPSD)
      val sparseDataBuffer = toGPUBuffer()
      val numWorkgroups = ceil((size / 2.0) / SPARSE_WRITER_WORKGROUP_SIZE).toInt()
      sparse_mat_load(sparseDataBuffer, outputBuffer)(numWorkgroups)
      sparseDataBuffer.destroy()
      return outputBuffer
    }

    fun List<Int>.toGPUBuffer(usage: Int = STCPSD): GPUBuffer = toTypedArray().toGPUBuffer(usage)
    fun List<UInt>.toGPUBuffer(usage: Int = STCPSD): GPUBuffer = map { it.toInt() }.toTypedArray().toGPUBuffer(usage)
    fun IntArray.toGPUBuffer(usage: Int = GPUBufferUsage.STORAGE or GPUBufferUsage.COPY_DST): GPUBuffer =
      GPUBuffer(size * 4, usage, unsafeCast<JSIntArray>())
    fun Int.toGPUBuffer(usage: Int = STCPSD): GPUBuffer = intArrayOf(this).toGPUBuffer(usage)
    fun Array<Int>.toGPUBuffer(usage: Int = STCPSD): GPUBuffer =
      JSIntArray(size).apply { set(this@toGPUBuffer, 0) }
        .let { GPUBuffer(byteSize = size * 4, us = usage, data = it) }

    // TODO: figure out map/unmap lifetime?
    fun GPUBuffer(byteSize: Number, us: Int, data: AllowSharedBufferSource? = null): GPUBuffer =
      gpu.createBuffer(
        descriptor = GPUBufferDescriptor(
          size = byteSize.toDouble(),
          usage = us.unsafeCast<web.gpu.GPUBufferUsage>()
        )
      )
        .also { if (data != null) { gpu.queue.writeBuffer(it, 0.0, data) } }

    private const val WORKGROUP_SIZE = 256

    fun prefixSumGPU(inputBuf: GPUBuffer, length: Int): GPUBuffer = GPUBufferScope().use { buffers ->
      val numBlocks = (length + WORKGROUP_SIZE - 1) / WORKGROUP_SIZE
      val groupsX   = minOf(numBlocks, DISPATCH_GROUP_SIZE_X)
      val groupsY   = (numBlocks + groupsX - 1) / groupsX

      val outputBuf    = buffers.newBuffer(inputBuf.size.toInt())
      val blockSumsBuf = buffers.newBuffer(numBlocks * 4)
      val uniBuf       = buffers.own(intArrayOf(length, numBlocks, groupsX)
        .toGPUBuffer(GPUBufferUsage.UNIFORM or GPUBufferUsage.COPY_DST))

      prefix_sum_p1(inputBuf, outputBuf, blockSumsBuf, uniBuf)(groupsX, groupsY)

      if (numBlocks > 1) {
        val scannedBlockSumsBuf = buffers.own(prefixSumGPU(blockSumsBuf, numBlocks))
        prefix_sum_p2(outputBuf, scannedBlockSumsBuf, uniBuf)(groupsX, groupsY)
      }
      buffers.detach(outputBuf)
    }

    suspend fun packMetadata(cfg: CFG, fsa: FSA): GPUBuffer {
      val t0 = TimeSource.Monotonic.markNow()

      val ge = cfg.grammarEncoding
      val grammarFlattened = ge.flat.toGPUBuffer(STCPSD)
      val grammarOffsets = ge.offsets.toGPUBuffer(STCPSD)

      val gle = cfg.groupedLeftAdjEncoding
      val leftAdjFlattened = gle.flat.toGPUBuffer(STCPSD)
      val leftAdjOffsets = gle.offsets.toGPUBuffer(STCPSD)

      val activeWords = (cfg.nonterminals.size + 31) ushr 5

      log("Encoded grammar in ${t0.elapsedNow()}")

      val (reachBuf, _) = dag_reach.invokeDAGFixpoint(fsa)
      log("DAG fixpoint in ${t0.elapsedNow()}")

      val (allFSAPairsFlattened, allFSAPairsOffsets) =
        buildMidpointsGPU(fsa.numStates, reachBuf).also { reachBuf.destroy() }
      log("Sparse reachability took ${t0.elapsedNow()} / (${4 * (allFSAPairsFlattened.size + allFSAPairsOffsets.size)} bytes)")

      /** Memory layout: [CFL_STRUCT] */
      val metaBuf = packStruct(
        constants = listOf(fsa.numStates, cfg.nonterminals.size, activeWords),
        allFSAPairsFlattened,
        allFSAPairsOffsets,
        fsa.levFinalIdxs.toGPUBuffer(),
        grammarFlattened,
        grammarOffsets,
        leftAdjFlattened,
        leftAdjOffsets
      )

      log("Packed metadata in ${t0.elapsedNow()}")
      return metaBuf
    }

    suspend fun buildMidpointsGPU(states: Int, reachBuf: GPUBuffer): Pair<GPUBuffer, GPUBuffer> {
      val totalPairs = states * states
      val cntBuf     = GPUBuffer(totalPairs * 4, STCPSD)
      val uniBuf     = states.toGPUBuffer(GPUBufferUsage.UNIFORM or GPUBufferUsage.COPY_DST)

      mdpt_count(reachBuf, cntBuf, uniBuf)(states, states)
      val offBuf = prefixSumGPU(cntBuf, totalPairs)
      val last   = listOf(totalPairs - 1)
      val totalM = offBuf.readIndices(last)[0] + cntBuf.readIndices(last)[0]
      val flatBuf = GPUBuffer(maxOf(4, totalM * 4), STCPSD)
      mdpt_write(reachBuf, offBuf, flatBuf, uniBuf)(states, states)

      return (flatBuf to offBuf).also { uniBuf.destroy(); cntBuf.destroy() }
    }

    suspend fun buildBackpointers(numStates: Int, numNTs: Int, dpIn: GPUBuffer, metaBuf: GPUBuffer): Triple<GPUBuffer, GPUBuffer, GPUBuffer> {
      val totalCells = numStates * numStates * numNTs

      val bpCountBuf = GPUBuffer(totalCells * 4, STCPSD)
      val ntWorkgroups = (numNTs + DENSE_NT_WORKGROUP_SIZE - 1) / DENSE_NT_WORKGROUP_SIZE

      log("Total cells: $totalCells = $numStates^2 * $numNTs")
      bp_count(dpIn, bpCountBuf, metaBuf)(numStates, numStates, ntWorkgroups)

//    val bpOffsetBuf = bpCountBuf.readInts().scan(0) { acc, arr -> acc + arr }.dropLast(1).toIntArray().toGPUBuffer(STCPSD)
      val bpOffsetBuf = prefixSumGPU(bpCountBuf, totalCells)

      val lastIdx = listOf(totalCells - 1)
      val totalExpansions = bpOffsetBuf.readIndices(lastIdx)[0] + bpCountBuf.readIndices(lastIdx)[0]
      log("Total expansions: $totalExpansions")

      val bpStorageBuf = GPUBuffer(maxOf(8, totalExpansions * 2 * 4), STCPSD)

      bp_write(dpIn, bpOffsetBuf, bpStorageBuf, metaBuf)(numStates, numStates, ntWorkgroups)

      return Triple(bpCountBuf, bpOffsetBuf, bpStorageBuf)
    }

    fun buildLanguageSizeCDF(
      nStates: Int,
      nNT: Int,
      dpIn: GPUBuffer,
      metaBuf: GPUBuffer,
      tmBuf: GPUBuffer,
      bpCountBuf: GPUBuffer,
      bpOffsetBuf: GPUBuffer,
      bpStorageBuf: GPUBuffer,
      cdfBuf: GPUBuffer
    ) {
      for (span in 1..<nStates) {
        val spanBuf = span.toGPUBuffer(GPUBufferUsage.UNIFORM or GPUBufferUsage.COPY_DST)

        ls_cdf(
          dpIn, metaBuf, tmBuf, spanBuf,
          bpCountBuf, bpOffsetBuf, bpStorageBuf, cdfBuf
        )(nStates - span, 1, nNT)
        spanBuf.destroy()
      }
    }
  }

  // Invocation strategies: eliminates some of the ceremony of calling a GSL shader
  suspend fun invokeCFLFixpoint(numStates: Int, dpIn: GPUBuffer, activeBuf: GPUBuffer, metaBuf: GPUBuffer): Unit =
    GPUBufferScope().use { buffers ->
      var t0 = TimeSource.Monotonic.markNow()
      val iterationBuf = buffers.own(intArrayOf(0, 0).toGPUBuffer(STCPSD))
      val changedCellsBuf = buffers.newBuffer(numStates * numStates * 4)
      val binaryNtsBuf = buffers.newBuffer(activeBuf.size)
      val changeIndex = listOf(0)
      val workgroupsX = (numStates + CFL_WORKGROUP_SIZE_X - 1) / CFL_WORKGROUP_SIZE_X
      val workgroupsY = (numStates + CFL_WORKGROUP_SIZE_Y - 1) / CFL_WORKGROUP_SIZE_Y

      for (round in 0..<numStates) {
        if (round != 0) iterationBuf.writeU32(wordIndex = 0, value = 0)
        iterationBuf.writeU32(wordIndex = 1, value = round)

        cfl_mul_upper(
          dpIn, activeBuf, metaBuf, iterationBuf, changedCellsBuf, binaryNtsBuf
        )(workgroupsX, workgroupsY)
        val changesThisRound = iterationBuf.readIndices(changeIndex)[0]
        log("Round=$round, changes=$changesThisRound, time=${t0.elapsedNow()}")
        t0 = TimeSource.Monotonic.markNow()

        if (changesThisRound == 0) break
      }
    }

  suspend fun invokeDAGFixpoint(fsa: FSA): Pair<GPUBuffer, Int> {
    val adjList = fsa.adjList
    val states = fsa.numStates
    val input = adjList.toSquareMatrixSparse(states)
//    var t0 = TimeSource.Monotonic.markNow()
    var prevValue = -1

    for (round in 0..<states) {
      val changesBuf = 0.toGPUBuffer()
      dag_reach(input, changesBuf)(states, states)
      val changesThisRound = changesBuf.readIndices(listOf(0))[0]
      changesBuf.destroy()
      if (changesThisRound == prevValue) break
      prevValue = changesThisRound
//      log("Round=$round, changes=$changesThisRound, time=${t0.elapsedNow()}")
//      t0 = TimeSource.Monotonic.markNow()
    }

    return input to prevValue
  }

  class DispatchStrategy(val gce: GPUCommandEncoder, val gcpe: GPUComputePassEncoder) {
    fun dispatchFlat(workgroups: Int) = invoke(
      minOf(workgroups, DISPATCH_GROUP_SIZE_X),
      (workgroups + DISPATCH_GROUP_SIZE_X - 1) / DISPATCH_GROUP_SIZE_X
    )

    operator fun invoke(x: Int, y: Int = 1, z: Int = 1) {
      gcpe.dispatchWorkgroups(x, y, z)
      gcpe.end()
      gpu.queue.submit(arrayOf(gce.finish()))
    }
  }

  operator fun invoke(vararg inputs: GPUBuffer): DispatchStrategy {
    check(::pipeline.isInitialized) { "Shader '$name' was used before bind()" }
    return gpu.createCommandEncoder().let { gce ->
      gce.beginComputePass().let { gcpe ->
        gcpe.setPipeline(pipeline)
        gcpe.setBindGroup(0, pipeline.bindBuffers("$name.buffers", *inputs))
        DispatchStrategy(gce, gcpe)
      }
    }
  }
}

// constants   = [c0,c1,…]
// buffers[i]  = payload_i   (u32‑packed GPUBuffer)
// result      = [constants | (off0,len0) (off1,len1)… | payload_0 … payload_k ]
//                ^ headerInts.size * 4  bytes
fun packStruct(constants: List<Int> = emptyList(), vararg buffers: GPUBuffer): GPUBuffer =
  packStructInternal(constants, true, buffers)

private fun packStructInternal(
  constants: List<Int> = emptyList(),
  destroyInputs: Boolean,
  buffers: Array<out GPUBuffer>
): GPUBuffer = GPUBufferScope().use { owned ->
  if (destroyInputs) owned.own(*buffers)
  if (buffers.isEmpty()) error("At least one payload buffer required")

  // ── lengths & offsets (in *ints*, not bytes) ──────────────────────────────
  val lens     = buffers.map { (it.size / 4).toInt() }
  val offsets  = lens.runningFold(0) { acc, len -> acc + len }.dropLast(1)

  // ── build header ints ─────────────────────────────────────────────────────
  val headerInts = buildList {
    addAll(constants)
    for (i in lens.indices) { add(offsets[i]); add(lens[i]) }
  }

  val headerBytes  = headerInts.size * 4
  val payloadBytes = lens.sum()      * 4
  val totalBytes   = headerBytes + payloadBytes

  // ── allocate destination buffer ───────────────────────────────────────────
  val metaBuf = owned.newBuffer(totalBytes)

  // ── upload header (one writeBuffer) ───────────────────────────────────────
  gpu.queue.writeBuffer(metaBuf, 0.0, JSIntArray(headerInts.size).apply { set(headerInts.toTypedArray(), 0) })

  // ── stitch payloads in place with a single CommandEncoder ────────────────
  val enc = gpu.createCommandEncoder()
  for (i in buffers.indices) {
    val dstOffBytes = headerBytes + offsets[i] * 4
    enc.copyBufferToBuffer(buffers[i], 0.0, metaBuf, dstOffBytes.toDouble(), buffers[i].size)
  }

  gpu.queue.submit(arrayOf(enc.finish()))

  owned.detach(metaBuf)
}

// Owns temporary GPU buffers until the surrounding use block exits.
internal class GPUBufferScope : AutoCloseable {
  private val buffers = mutableListOf<GPUBuffer>()
  fun newBuffer(byteSize: Number, usage: Int = STCPSD): GPUBuffer = own(GPUBuffer(byteSize, usage))
  fun own(buffer: GPUBuffer): GPUBuffer = buffer.also { buffers += it }
  fun own(vararg buffers: GPUBuffer) { this.buffers.addAll(buffers) }
  /** Transfers a returned buffer to the caller without destroying it. */
  fun detach(buffer: GPUBuffer): GPUBuffer = buffer
    .also { check(buffers.remove(it)) { "Cannot transfer a buffer this scope does not own" } }
  /** Release owned intermediates after their last submitted GPU operation. */
  fun release(vararg buffers: GPUBuffer) = buffers.forEach { detach(it).destroy() }
  override fun close() = buffers.asReversed().forEach(GPUBuffer::destroy)
}

const val NEWLINE_ID = 1
const val BOS_ID     = 2
const val EOS_ID     = 3
const val FIRST_TID  = 4

fun tmToInt(tm: String, cfg: CFG): Int = when (tm) {
  "NEWLINE" -> NEWLINE_ID
  "BOS"     -> BOS_ID
  "EOS"     -> EOS_ID
  else      -> cfg.tmMap[tm]!! + FIRST_TID
}

const val SCALE = 10_000.0
fun Map<List<String>, Double>.toGpuHash(norm: Double = values.sum(), cfg: CFG): Map<List<UInt>, UInt> =
  mapValues { (_, p) -> (-ln(p / norm) * SCALE).roundToInt().coerceAtLeast(0).toUInt() }
    .mapKeys { (gram, _) -> gram.map { tmToInt(it, cfg).toUInt() } }

fun Map<List<UInt>, UInt>.loadToGPUBuffer(loadFactor: Double = 0.75): GPUBuffer {
  require(all { it.key.size == 4 }) { "Only 4-grams are supported" }

  val offset = FIRST_TID.toUInt()

  /** Compresses a 4‑gram (tokens are 1‑based) into one u32: 4 × 7‑bit fields. */
  fun packGram(g: List<UInt>): UInt =
    ((g[0] - offset) shl 21) or ((g[1] - offset) shl 14) or
        ((g[2] - offset) shl 7)  or  (g[3] - offset)

  /* ── pick a power‑of‑two table size ─────────────────────────────────── */
  val nEntries = size.coerceAtLeast(1)
  var pow = 1
  while ((1 shl pow) < (nEntries / loadFactor).roundToInt()) pow++
  val slots = 1u shl pow
  val mask  = slots - 1u

  val table = UIntArray(slots.toInt() * 2) { SENTINEL }

  /* ── insert with linear probing ─────────────────────────────────────── */
  for ((gram, score) in this) {
    val key  = packGram(gram)
    var slot = ((key * HASH_MUL) shr (32 - pow)) and mask

    while (table[(slot * 2u).toInt()] != SENTINEL) { slot = (slot + 1u) and mask }
    val idx = (slot * 2u).toInt()
    table[idx]     = key
    table[idx + 1] = score
  }

  /* ── prepend header (pow) and upload ────────────────────────────────── */
  val flat = UIntArray(1 + table.size)
  flat[0] = pow.toUInt()
  table.copyInto(flat, 1)

  log("Done")

  return flat.asIntArray().toGPUBuffer(STCPSD)
}

const val PROFILE_WGPU_KERNELS = true

suspend fun awaitGPUQueue() = gpu.queue.asDynamic().onSubmittedWorkDone().unsafeCast<Promise<*>>().await()

// Measures only the work submitted inside `block`, not older queued work.
internal suspend inline fun timedGPUIsolated(label: String, block: () -> Unit): Int {
  if (PROFILE_WGPU_KERNELS) awaitGPUQueue() // drain previous queued work

  val t = TimeSource.Monotonic.markNow()
  block()

  if (PROFILE_WGPU_KERNELS) awaitGPUQueue() // wait for this block's submitted work

  val ms = t.elapsedNow().inWholeMilliseconds.toInt()
  if (PROFILE_WGPU_KERNELS) log("$label in ${ms}ms")
  return ms
}